import { useEffect } from "react";
import { toast } from "sonner";
import { playbookGoals } from "@/pages/UserHub/Playbooks/data/playbook-templates";
import { MarketplaceService } from "@/services/marketplace/MarketplaceService";

import type { Dispatch } from "react";
export const useTemplatePreload = (
  templateId: string | null,
  setupPhase: string,
  connected: boolean,
  dispatch: Dispatch<any>,
) => {
  useEffect(() => {
    if (!templateId || setupPhase !== "conversation" || connected) return;

    const loadTemplate = async () => {
      if (templateId.startsWith("marketplace-")) {
        const marketplaceSlug = templateId.replace("marketplace-", "");

        try {
          const marketplaceGenie = await MarketplaceService.getMarketplaceGenie(marketplaceSlug);

          if (marketplaceGenie && marketplaceGenie.agent) {
            const { genie, agent } = marketplaceGenie;
            const agentConfig = agent.settings?.conversation_config;

            const analysisFromMarketplace = {
              genieName: genie.display_name,
              useCase: genie.tagline,
              description: genie.description,
              category: genie.category_name || "general",
              language: agentConfig?.agent?.language || "en",
              voicePreference: agentConfig?.tts?.voice_id || "professional",
              conversationSettings: {
                maxDuration: agentConfig?.conversation?.max_duration_seconds || 1800,
                turnTimeout: agentConfig?.turn?.turn_timeout || 20,
                silenceTimeout: agentConfig?.turn?.silence_end_call_timeout || 300,
                turnMode: (agentConfig?.turn?.mode || "silence") as "silence" | "turn",
              },
              systemPrompt: agentConfig?.agent?.prompt?.prompt || "",
              firstMessage: agentConfig?.agent?.first_message || "",
              confidence: 1.0,
              sendConversationReports: true,
              reportMode: "every_conversation" as const,
              ...(genie.metadata?.features && { features: genie.metadata.features }),
              supportEmails: [],
              pronunciations: [],
            };

            dispatch({ type: "SET_ANALYSIS_RESULT", payload: analysisFromMarketplace });
            dispatch({ type: "SET_PHASE", payload: "confirmation" });
            toast.success(`Loaded "${genie.display_name}" from marketplace - customize it below`);
          }
        } catch (error) {
          console.error("Error loading marketplace template:", error);
          toast.error("Failed to load marketplace template");
        }
        return;
      }

      const parentGoal = playbookGoals.find((goal) => goal.plays.some((play) => play.id === templateId));
      const template = parentGoal?.plays.find((play) => play.id === templateId);

      if (template && template.templateConfig) {
        const analysisFromTemplate = {
          genieName: template.name,
          useCase: template.tagline,
          language: "en",
          voicePreference: "professional",
          conversationSettings: {
            maxDuration: 60 * 30, // 30 minutes,
            turnTimeout: 20,
            silenceTimeout: 300,
            turnMode: "silence" as "silence" | "turn",
          },
          systemPrompt: template.templateConfig.prompt || "",
          firstMessage: template.templateConfig.first_message || "",
          confidence: 1.0,
          category: parentGoal?.id ?? "general",
          description: template.strategy,
          sendConversationReports: true,
          reportMode: "every_conversation" as const,
          tools: template.templateConfig.tools,
          knowledgeBaseId: template.templateConfig.knowledge_base_id,
          supportEmails: [],
          pronunciations: [],
        };

        dispatch({ type: "SET_ANALYSIS_RESULT", payload: analysisFromTemplate });
        dispatch({ type: "SET_PHASE", payload: "confirmation" });
        toast.success(`Loaded "${template.name}" playbook template - customize it below`);
      }
    };

    loadTemplate();
  }, [templateId, setupPhase, connected, dispatch]);
};
